// src/components/common/NotificationBar.js
import React, { useState, useEffect, useCallback } from 'react';
import './NotificationBar.css';

// Liste de vos messages promotionnels (ceux que vous avez fournis)
const PROMOTIONAL_MESSAGES = [
  { id: "promo_hiver_2025", text: "❄️ Offres d'hiver exclusives ! Jusqu'à -5% sur toutes nos offres." },
  { id: "promo_nouveautes_juin", text: "🚀 Mises à jour offertes pendant 1 an !" },
  { id: "promo_livraison_gratuite", text: "🚚 Livraison entre 2 et 3 semaine de la conception au rendu final." },
  { id: "promo_fidelite_club", text: "🌟 Rejoignez notre Club Fidélité et profitez d'avantages uniques !" },
  { id: "promo_parrainage_amis", text: "🤝 Parrainez un ami et recevez un bon d'achat de 100€ chacun !" }
];

const SESSION_STORAGE_KEY = "notificationBar_currentIndex_v1";

function NotificationBar({ messages = PROMOTIONAL_MESSAGES, intervalMs = 5000 }) {
  const [isVisible, setIsVisible] = useState(false); // Gère la visibilité initiale de la barre
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  // Chargement de l'index initial et affichage de la barre
  useEffect(() => {
    if (!messages || messages.length === 0) {
      setIsVisible(false); // Cache la barre s'il n'y a pas de messages
      return;
    }

    const savedIndexStr = sessionStorage.getItem(SESSION_STORAGE_KEY);
    let initialIndex = 0;
    if (savedIndexStr !== null) {
      const parsedIndex = parseInt(savedIndexStr, 10);
      if (parsedIndex >= 0 && parsedIndex < messages.length) {
        initialIndex = parsedIndex;
      } else {
        sessionStorage.setItem(SESSION_STORAGE_KEY, '0'); // Réinitialise si index invalide
      }
    }
    
    setCurrentMessageIndex(initialIndex);
    // La barre apparaît (peut-être avec son animation CSS initiale si .hidden est retiré)
    // Un léger délai peut être conservé pour l'apparition initiale si souhaité.
    const timer = setTimeout(() => setIsVisible(true), 50); 
    return () => clearTimeout(timer);

  }, [messages]);

  // Rotation automatique (même effet que le clic sur la croix)
  useEffect(() => {
    if (!isVisible) return;
    if (!messages || messages.length <= 1) return;
    const ms = Math.max(1000, Number(intervalMs) || 5000);
    const t = setInterval(() => {
      setCurrentMessageIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % messages.length;
        sessionStorage.setItem(SESSION_STORAGE_KEY, nextIndex.toString());
        return nextIndex;
      });
    }, ms);
    return () => clearInterval(t);
  }, [isVisible, messages, intervalMs]);

  const handleDismiss = useCallback(() => {
    if (!messages || messages.length === 0) return;

    // Change simplement l'index du message. 
    // La barre reste visible (isVisible n'est pas modifié ici).
    // Le changement de currentMessageIndex va changer la `key` du div principal,
    // ce qui forcera son re-montage et l'animation du texte enfant.
    setCurrentMessageIndex(prevIndex => {
      const nextIndex = (prevIndex + 1) % messages.length;
      sessionStorage.setItem(SESSION_STORAGE_KEY, nextIndex.toString());
      return nextIndex;
    });
  }, [messages]);


  if (!messages || messages.length === 0) {
    return null;
  }

  const currentMessageObject = messages[currentMessageIndex];

  if (!currentMessageObject || !isVisible) { 
    // Ne rien rendre si pas de message ou si la barre n'est pas encore visible
    return null;
  }

  return (
    <div 
      // Changer la key force le re-montage du composant (ou de cet élément et ses enfants).
      // Cela permet à l'animation CSS sur .notification-message de se redéclencher.
      key={currentMessageObject.id} 
      className={`notification-bar ${!isVisible ? 'hidden' : ''}`}
      role="alert"
      aria-live="polite" 
    >
      <span className="notification-message"> {/* L'animation sera sur cet élément */}
        {currentMessageObject.text}
      </span>
      <button 
        onClick={handleDismiss} 
        className="notification-close-button"
        aria-label="Fermer la notification" 
      >
        &times;
      </button>
    </div>
  );
}

export default NotificationBar;